export type PortalUser = {
  id: number;
  email: string;
  name: string;
  role: string;
  authorIds: number[];
};

export type AuthProvider = {
  id: string;
  label: string;
  configured: boolean;
};

export async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(path, { credentials: "include" });
  return parseApiResponse<T>(response);
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Castlehale-CSRF": "1"
    },
    body: JSON.stringify(body)
  });
  return parseApiResponse<T>(response);
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "PATCH",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Castlehale-CSRF": "1"
    },
    body: JSON.stringify(body)
  });
  return parseApiResponse<T>(response);
}

export async function apiUpload<T>(path: string, body: FormData): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: {
      "X-Castlehale-CSRF": "1"
    },
    body
  });
  return parseApiResponse<T>(response);
}

export function authStart(provider: string, redirect = window.location.pathname) {
  window.location.href = `/api/auth/oauth/${provider}/start?redirect=${encodeURIComponent(redirect)}`;
}

async function parseApiResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    const trimmed = text.trimStart();
    const isHtml = /^<(?:!doctype|html)\b/i.test(trimmed);
    throw new Error(
      isHtml
        ? "Castlehale API is unavailable. Check the Cloudflare Pages Function logs and bindings."
        : text || `${response.status} ${response.statusText}`
    );
  }

  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : {};
  } catch {
    throw new Error("Castlehale API returned malformed JSON.");
  }

  if (!response.ok) {
    const message = typeof parsed === "object" && parsed && "error" in parsed ? String((parsed as { error: unknown }).error) : `${response.status} ${response.statusText}`;
    throw new Error(message);
  }

  return parsed as T;
}
